<!-- Part of the SPARKL educational activity system, Copyright 2019 by Pepper Williams -->
<template><div>
	<div v-if="unit.standards.length==0" class="pa-2 text-center"><i>This unit does not have any standards specified.</i></div>
	<div class="mx-1 my-2" v-if="unit.standards.length>0">
		<div class="d-flex">
			<div v-if="site_config.hide_unit_view_header!='yes'" class="k-unit-view-header ml-2">{{ standards_button_text }}</div>
		</div>
		<div class="k-lp-unit-description mt-2 ml-3 mr-3">
			<div v-if="!standards_tree_loaded" class="pa-4" style="font-size:16px">Loading...</div>

			<div v-for="(standards, fr_id) in unit_standards_tree" class="k-lp-unit-standards-framework-wrapper">
				<div class="k-lp-unit-standards-framework">
					{{framework_title(fr_id)}}
				</div>
				<div class="k-lp-unit-standards-framework-inner-wrapper">
					<div v-for="(o,i) in standards" class="k-lp-unit-standard" :style="standard_style(o,i)" :class="standard_class(o)" v-show="standard_showing[o.cfitem.identifier]">
						<div class="k-lp-unit-standard-statement-wrapper">
							<div style="flex:0 0 26px; margin:-1px 0 0 0;">
								<k-icon v-if="o.children.length>0" style="font-size:17px;" color="#666" @click.stop="toggle_children(o)">fa-fw fas {{standard_children_showing(o) ? 'fa-circle-chevron-down' : 'fa-circle-chevron-right'}}</k-icon>
								<k-icon v-else style="font-size:17px;" color="#666">fa-fw fas fa-circle-dot</k-icon>
							</div>
							<div class="k-lp-unit-standard-statement" :class="o.children.length>0?'k-lp-unit-standard-statement-node':''" @click.stop="(o.children.length>0)?toggle_children(o):null"><b v-if="o.cfitem.humanCodingScheme" style="margin-right:6px" v-html="o.cfitem.humanCodingScheme"></b><span class="k-case-tree-item-statement" v-html="U.marked_latex(o.cfitem.fullStatement)"></span></div>
							<k-spacer/>
							<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-on="on" icon small color="secondary" style="margin:-2px 6px 0 4px" @click.stop="show_standard_in_satchel(o.cfitem)"><k-icon small>fas fa-map</k-icon></k-btn></template>Show in standards framework</k-tooltip>
							<div v-if="some_resources_included" style="flex:0 0 88px" class="text-center">
								<k-btn x-small :disabled="o.asset_count==0" class="k-tight-btn k-nocaps-btn" color="secondary" :outlined="standard_resources_showing[o.cfitem.identifier]" @click.stop="resource_search_by_standard(o)">{{o.asset_count}} {{U.ps('Resource', o.asset_count)}}<k-icon x-small class="ml-1">fas fa-search</k-icon></k-btn>
							</div>
						</div>
					</div>
				</div>
			</div>

		</div>
	</div>
	<div v-if="user_can_edit_unit" class="text-center mt-4">
		<k-btn small class="k-edit-btn xk-tight-btn xk-nocaps-btn mx-1 mb-3 elevation-0" color="#ddd" @click="$emit('edit_unit')"><k-icon class="mr-2" small>fas fa-edit</k-icon>Edit Unit</k-btn>
	</div>
</div></template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
	components: {  },
	props: {
		lp: { type: Object, required: true },
		unit: { type: Object, required: true },
		is_collection_admin: { type: Boolean, required: true },
	},
	data() { return {
		standards_tree_loaded: false,
		max_level: 0,
		// tried_to_load_lsdoc_list: false,
		// tried_to_load_framework: false,
		update_standards_count_trigger: 0,
		some_resources_included: false,
	}},
	computed: {
		...mapState(['user_info', 'my_lessons', 'my_resources', 'site_config', 'framework_asset_counts']),
		...mapGetters(['immediately_show_standards_aligned_resources']),
		standards_button_text() { return this.site_config.unit_mode_toggle_option?.standards || 'Standards' },
		standard_showing() { return this.unit.standard_showing },
		standard_resources_showing() { return this.unit.standard_resources_showing },
		standard_secondary_resources_showing() { return this.unit.standard_secondary_resources_showing },
		is_default_collection() { return (this.lp.course_code == 'default') },
		user_can_edit_unit() {
			return (this.is_collection_admin && !this.is_default_collection)
		},
		unit_standards_tree() {
			// we need to load asset counts by standard to be able to formulate this properly; this statement simply makes sure that we will re-run this fn after loading the counts, if we need to load the counts
			if (this.update_standards_count_trigger < 0) return

			// we'll also determine here if some_resources_included
			this.some_resources_included = false

			// for our purposes in constructing asset lists, treat two assets as equivalent (and therefore only list one of them) if...
			let asset_already_in_list = (list, a2) => {
				return false
				for (let a1 of list) {
					if (a2.asset_type == 'lesson') {
						if (a1.asset_type != 'lesson') continue
						// for lessons, we'll just check the lesson_titles
						if (a1.lesson.lesson_title == a2.lesson.lesson_title) return true
					
					} else {
						if (a1.asset_type != 'resource') continue
						// for resources, check urls
						if (a1.resource.url == a2.resource.url) return true
					}
				}

				// if we get to here, the asset isn't already in the list
			}

			this.max_level = 0
			// Note: we used to load the framework from SuitCASE here; but as of 10/2024 that's no longer necessary, because we're keeping the standards in synch with SuitCASE on the back end

			// we might be getting standards from multiple frameworks; start by sorting by framework
			let frameworks = {}
			for (let standard of this.unit.standards) {
				let framework_identifier = standard.framework_identifier || 'unknown'
				if (empty(frameworks[framework_identifier])) {
					frameworks[framework_identifier] = []
				}
				frameworks[framework_identifier].push(standard)
			}

			// now go through each framework's standards
			for (let framework_identifier in frameworks) {
				let framework_standards = frameworks[framework_identifier]

				let top_items = []

				// start with all items that aren't children of other items, in the order they were loaded
				let last_child_of = ''
				let all_top_level_are_siblings = true
				for (let standard of framework_standards) {
					if (empty(standard.childOf) || !framework_standards.find(x=>x.identifier == standard.childOf)) {
						top_items.push({standard:standard, children:[]})
						if (empty(last_child_of)) last_child_of = standard.childOf
						else if (standard.childOf != last_child_of) all_top_level_are_siblings = false
					}
				}

				// if top_items items are all children of the same item, order by sequenceNumber
				// NO, for now at least, *always* order top_items by sequenceNumber
				// if (!empty(last_child_of) && all_top_level_are_siblings) {
					top_items.sort((a,b)=>a.standard.sequenceNumber - b.standard.sequenceNumber)
				// }

				// recursive fn for creating a tree structure for the standards
				let add_descendents = (parent) => {
					for (let standard of framework_standards) {
						if (standard.childOf == parent.standard.identifier) {
							parent.children.push({standard:standard, children:[]})
						}
					}
					// sort by sequenceNumber
					parent.children.sort((a,b)=>a.standard.sequenceNumber - b.standard.sequenceNumber)

					// add descendents of children
					for (let child of parent.children) {
						add_descendents(child)
					}
				}

				// now for each item in top_items, add its descendents
				for (let o of top_items) {
					add_descendents(o)
				}

				let arr = []

				let add_item = (parent, level) => {
					// do some calculations while we're going through all the items here...
					if (level > this.max_level) this.max_level = level

					// reveal the first X levels of children
					let levels_to_show = 1
					if (this.standard_showing[parent.standard.identifier] === undefined) {
						this.$set(this.standard_showing, parent.standard.identifier, (level <= levels_to_show))
					}

					// reveal resources for all levels if immediately_show_standards_aligned_resources is true
					if (this.immediately_show_standards_aligned_resources == true) {
						if (this.standard_resources_showing[parent.standard.identifier] == undefined) this.$set(this.standard_resources_showing, parent.standard.identifier, true)
					}

					// get asset_count for the standard; load framework_asset_counts as we go if needed
					let asset_count = 0
					if (!empty(parent.standard.framework_identifier)) {
						let fac = this.framework_asset_counts[parent.standard.framework_identifier]
						// try to load the asset_counts and return; Vue will send control back here and re-calculate when done
						if (fac == 'loading') return -1
						if (empty(fac)) {
							this.$store.dispatch('get_framework_asset_counts', parent.standard.framework_identifier).then(x=>{
								++this.update_standards_count_trigger	// this triggers this computed to re-compute
							})
							return -1
						}

						// if we get to here we have the asset_counts, so add for this standard
						asset_count += (fac[parent.standard.identifier] || 0)
					}

					// construct the parent item object
					let p = {
						cfitem: parent.standard, 
						children: parent.children,
						level: level,
						asset_count: asset_count,
					}
					arr.push(p)

					if (p.asset_count > 0) this.some_resources_included = true

					// add children
					for (let child of parent.children) {
						let cr = add_item(child, level+1)
						// add child's asset_count to parent's asset_count, but don't add the same asset twice TODO: ?????????
						p.asset_count += cr.asset_count
					}

					return p
				}
				for (let item of top_items) add_item(item, 0)

				// record this framework's standards
				frameworks[framework_identifier] = arr
			}

			this.standards_tree_loaded = true
			return frameworks
		},
		collection_view_mode: {
			get() {
				if (this.lp.collection_type === 'pd') return 'list'
				return this.$store.state.lst.collection_view_mode
			},
			set(val) { this.$store.commit('lst_set', ['collection_view_mode', val]) }
		},
	},
	watch: {
	},
	created() {
	},
	mounted() {
		vapp.collection_unit_standards_component = this
	},
	methods: {
		framework_title(fid) {
			// we should have aligned standard frameworks in state.subjects...
			for (let subject in this.$store.state.subjects) {
				let so = this.$store.state.subjects[subject]
				if (so.framework_identifier == fid) {
					// if we find the framework, if a framework_title is explicitly specified, use it
					if (!empty(so.framework_title)) return so.framework_title
					// otherwise use the subject name
					else return `${subject} Standards`
				}
			}
			return ''
		},

		standard_children_showing(o) {
			// if the item has children and one of them is showing, the item's children are showing
			return (o.children.length > 0 && this.standard_showing[o.children[0].standard.identifier])
		},

		toggle_children(o, val) {
			// by default we toggle, but allow val to be specified directly
			if (typeof(val) != 'boolean') val = !this.standard_children_showing(o)

			// if new val is false, hide children, as well as their grandchildren
			if (!val) {
				for (let child of o.children) {
					// have to recurse first here, before we change standard_showing for the child
					this.toggle_children(child, false)

					// for some reason extobj (which uses $.extend) works here, whereas object_copy (which uses JSON.stringify) does not -- object_copy(this.standard_showing) produces [] every time
					let ss = extobj(this.standard_showing)
					ss[child.standard.identifier] = false
					this.$store.commit('set', [this.unit, 'standard_showing', ss])
				}

			// else show children
			} else {
				let ss = extobj(this.standard_showing)
				for (let child of o.children) ss[child.standard.identifier] = true
				this.$store.commit('set', [this.unit, 'standard_showing', ss])
			}
		},

		standard_style(o, i) {
			let s = ''
			s += `padding-left:${8+o.level*20}px;`

			// tweak border for first item
			if (i == 0) s += 'margin-top:-1px'

			return s
		},

		standard_class(o) {
			let bcolors
			if (this.max_level == 0) bcolors = [0]
			else if (this.max_level == 1) bcolors = [0, 4]
			else if (this.max_level == 2) bcolors = [0, 4, 5]
			else if (this.max_level == 3) bcolors = [0, 3, 4, 5]
			else if (this.max_level == 4) bcolors = [0, 2, 3, 4, 5]
			else if (this.max_level == 5) bcolors = [0, 2, 3, 4, 5, 6]
			else if (this.max_level == 6) bcolors = [0, 2, 3, 4, 5, 6, 7]
			else bcolors = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

			return `k-case-tree-item-type-color-${bcolors[o.level]}`
		},

		// show_resources_list_for_standard(o) {
		// 	// don't show (yet) if resources aren't yet fully loaded
		// 	if (!this.unit.standards_aligned_assets_loaded) return false

		// 	// don't show if we don't have any branch assets
		// 	if (o.branch_assets.length == 0) return false
			
		// 	// show if standard_resources_showing is true
		// 	if (this.standard_resources_showing[o.cfitem.identifier]) return true

		// 	return false
		// },

		show_standard_in_satchel(case_item) {
			// show the framework, highlight this item, and send in selected_items and limit_to_selected_items if there
			let data = { 
				framework_identifier: case_item.framework_identifier, 
				item_identifier: case_item.identifier, 
				no_framework_reset: true,
			}

			if (!data.framework_identifier) {
				// if case_item doesn't have a framework_identifier, it was probably selected before we started recording this.
				// so trace up through parents looking for a case_framework_identifier value; we should find it in in CourseView
				let parent = this.$parent
				while (parent) {
					if (parent.case_framework_identifier) { data.framework_identifier = parent.case_framework_identifier; break; }
					parent = parent.$parent
				}
			}

			// add all unit standards as selected_items
			if (this.unit.standards.length > 0) {
				data.selected_items = []
				for (let standard of this.unit.standards) data.selected_items.push(standard.identifier)
				data.limit_to_selected_items = 'only'
			}

			vapp.$refs.satchel.execute('show').then(()=>{
				vapp.$refs.satchel.execute('load_framework', data).then(()=>{
				})
			})
		},

		show_secondary_resources(o) {
			let srs = object_copy(this.standard_secondary_resources_showing)
			srs[o.cfitem.identifier] = !srs[o.cfitem.identifier]
			this.$store.commit('set', [this.unit, 'standard_secondary_resources_showing', srs])
		},

		resource_search_by_standard(o) {
			vapp.initiate_search({
				caller_component: this,
				dialog_title: 'Search Collection Content',
				home_collection: this.lp,
				home_unit: this.unit,
				default_source: 'home_collection',
				default_framework_identifier: o.cfitem.framework_identifier,
				starting_item_identifier: o.cfitem.identifier
			})
		},
	}
}
</script>

<style lang="scss">
.k-lp-unit-standards-framework-wrapper {
	margin-bottom:20px;
}

.k-lp-unit-standards-framework-inner-wrapper {
	border-top:3px solid #999;
	border-bottom:3px solid #999;
	border-left:3px solid #999;
	border-right:3px solid #999;
	border-radius:4px;
	max-width:840px;
	margin:0 auto;
}

.k-lp-unit-standards-framework {
	font-weight:bold;
	font-size:18px;
	line-height: 22px;
	margin-bottom:8px;
}

.k-lp-unit-standard {
	padding-top:4px;
	padding-bottom:4px;
	padding-right:12px;
	font-size:15px;
	line-height:19px;
	border-top:1px solid #bbb;
	border-bottom:1px solid #bbb;
	margin-bottom:-1px;
}

.k-lp-unit-standard-top-category {
	font-weight:bold;
	color:$v-deep-orange-darken-4;
	margin-top:8px;
}

.k-lp-unit-standard-statement-wrapper {
	display:flex;
	align-items: flex-start;
}

.k-lp-unit-standard-statement-node {
	cursor:pointer;
}
.k-lp-unit-standard-statement-node:hover {
	text-decoration:underline;
}

.k-lp-unit-standard-resources {
	background-color:#fff;
	font-size:14px;
	margin-left: 16px;
	margin-right:-2px;

	.k-resource-folder-resource-container-width-half {
		padding:2px 4px;
	}
}

.k-lp-unit-standard-resources.k-resource-folder-list {
	border:1px solid #999;
	margin-top:4px;
	margin-bottom:4px;
	border-radius:6px;
	overflow:hidden;
}

// from Satchel
.k-case-tree-item-statement {
	// see also k-case-tree-item-association-title in CASEItemAssociations
	display:inline;
	p:first-of-type {
		display:inline;
	}

	ul, ol {
		margin-top:4px;
	}

	table {
		border-collapse:collapse;
		margin:4px 0;
	}
	th, td {
		border:1px solid #ddd;
		padding:4px;
	}
	th {
		background-color:#f8f8f8;
	}
	td {
		background-color:#fff;
	}
	code {
		// display:inline-block;
		background-color:transparent!important;	// vuetify wants to use a light gray background color
		padding:0 2px!important;
		font-size:0.92em;	// this makes it a little bigger than what it would be otherwise
	}
}

.k-lp-unit-standard-children {
	margin-left:32px;
	margin-bottom:8px;
}

// from Satchel
.k-case-tree-item-type-color-0 { background-color: $v-blue-lighten-5; }
.k-case-tree-item-type-color-1 { background-color: $v-orange-lighten-5; }
.k-case-tree-item-type-color-2 { background-color: $v-indigo-lighten-5; }
.k-case-tree-item-type-color-3 { background-color: $v-lime-lighten-5; }
.k-case-tree-item-type-color-4 { background-color: $v-purple-lighten-5; }
.k-case-tree-item-type-color-5 { background-color: $v-green-lighten-5; }
.k-case-tree-item-type-color-6 { background-color: $v-brown-lighten-5; }
.k-case-tree-item-type-color-7 { background-color: $v-red-lighten-5; }
.k-case-tree-item-type-color-8 { background-color: $v-deep-purple-lighten-5; }
.k-case-tree-item-type-color-9 { background-color: $v-light-blue-lighten-5; }
.k-case-tree-item-type-color-10 { background-color: $v-teal-lighten-5; }
.k-case-tree-item-type-color-11 { background-color: $v-yellow-lighten-5; }
.k-case-tree-item-type-color-12 { background-color: $v-deep-orange-lighten-5; }

</style>
